// src/pages/user/UserLearnShowcase.tsx
// Route: /user/learn/:childId/showcase
//
// The online showcase from the family's side: the pitch video handed in on
// a showcase week, the panel's result (feedback, award, scores) and the
// certificate. A parent chooses whether it goes in the academy gallery,
// which only signed-in Future Smart Lab families can watch.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectRole } from "../../app/features/authSlice";
import { useChildMode } from "../../app/childMode";
import { errorText } from "../../app/api/learningApi";
import {
  SCORE_LABELS,
  useGetGalleryQuery,
  useGetGalleryVideoUrlMutation,
  useGetShowcaseQuery,
  useGetShowcaseVideoUrlMutation,
  useMarkShowcaseSeenMutation,
  useSetGalleryConsentMutation,
} from "../../app/api/showcaseApi";
import type { FamilyShowcaseEntry, GalleryItem } from "../../app/api/showcaseApi";
import MediaPlayer from "../../components/MediaPlayer";

import { ArrowLeft, GraduationCap, Play, Trophy } from "lucide-react";
const fmtDay = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : "";

const AGE_FILTERS = [
  { value: "", label: "All ages" },
  { value: "foundation_builders", label: "6–9" },
  { value: "explorers", label: "9–12" },
  { value: "innovators", label: "13–18" },
];

/* ─── Video that loads its signed URL on demand ──────────────────── */

function LazyVideo({ load, label = "Watch" }: { load: () => Promise<string>; label?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (url) return <MediaPlayer src={url} autoPlay />;
  return (
    <div>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            setUrl(await load());
          } catch (e) {
            setErr(errorText(e, "Could not load the video"));
          } finally {
            setBusy(false);
          }
        }}
        className="inline-flex items-center gap-1.5 rounded-xl bg-[#16213e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a] disabled:opacity-50"
      >
        {!busy && <Play className="h-3.5 w-3.5 fill-white" aria-hidden />}
        {busy ? "Loading…" : label}
      </button>
      {err && <p className="mt-2 text-xs text-[#b94a3a]">{err}</p>}
    </div>
  );
}

/* ─── Score bar ──────────────────────────────────────────────────── */

function ScoreRow({ label, value }: { label: string; value: number | null }) {
  const pct = value ? Math.round((value / 5) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between gap-3 text-xs">
        <span className="text-[#3b4560]">{label}</span>
        <span className="font-semibold text-[#16213e]">
          {value ? `${value.toFixed(1)} · ${SCORE_LABELS[Math.round(value)]}` : "—"}
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[#ebeff7]">
        <div className="h-1.5 rounded-full bg-[#1b2d5e]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ─── Gallery choice (parent decides; others see the state) ─────── */

function GalleryChoice({ childId, entry, isParent }: { childId: string; entry: FamilyShowcaseEntry; isParent: boolean }) {
  const [save, { isLoading }] = useSetGalleryConsentMutation();
  const [title, setTitle] = useState(entry.projectTitle ?? "");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const g = entry.gallery;

  const state = g.live
    ? `In the academy gallery since ${fmtDay(g.premiereAt)}.`
    : g.approved && g.premiereAt
      ? `Premieres in the gallery on ${fmtDay(g.premiereAt)}.`
      : g.consent
        ? "Waiting for the academy to add it to the gallery."
        : "Not in the gallery.";

  const submit = async (consent: boolean) => {
    setNote(null);
    try {
      const res = await save({ childId, entryId: entry.id, consent, projectTitle: title.trim() }).unwrap();
      setNote({ ok: true, text: res.message ?? "Saved" });
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not save") });
    }
  };

  return (
    <div className="rounded-2xl border border-[#e5e8ef] bg-[#f9fafc] p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">Academy gallery</p>
      <p className="mt-1 text-sm text-[#3b4560]">{state}</p>
      {isParent ? (
        <>
          <p className="mt-2 text-xs leading-5 text-[#5f6780]">
            With your permission, other Future Smart Lab families can watch this video after signing in. It is never
            on a public page, and it shows only the first name and your city. You can take it out at any time.
          </p>
          {!g.consent ? (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                placeholder="Project name, e.g. Bakery Portfolio"
                className="flex-1 rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
              />
              <button
                type="button"
                disabled={isLoading || !title.trim()}
                onClick={() => submit(true)}
                className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a] disabled:opacity-50"
              >
                Share with academy families
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={isLoading}
              onClick={() => submit(false)}
              className="mt-3 rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#b94a3a] hover:bg-[#fff5f3] disabled:opacity-50"
            >
              Take it out of the gallery
            </button>
          )}
        </>
      ) : (
        <p className="mt-1 text-xs text-[#8189a0]">Your parent decides whether your showcase goes in the gallery.</p>
      )}
      {note && <p className={`mt-2 text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</p>}
    </div>
  );
}

/* ─── One of the child's own showcases ──────────────────────────── */

function MyEntry({ childId, entry, isParent }: { childId: string; entry: FamilyShowcaseEntry; isParent: boolean }) {
  const [getUrl] = useGetShowcaseVideoUrlMutation();
  const [markSeen] = useMarkShowcaseSeenMutation();
  const r = entry.result;

  useEffect(() => {
    if (r && !r.seen) markSeen({ childId, entryId: entry.id });
  }, [r, childId, entry.id, markSeen]);

  return (
    <div className="space-y-4 rounded-[24px] border border-[#e5e8ef] bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">{entry.showcaseName}</p>
          <p className="text-lg font-bold text-[#16213e]">{entry.projectTitle || entry.lessonTitle}</p>
          <p className="text-xs text-[#8189a0]">Handed in {fmtDay(entry.submittedAt)}</p>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-[12.5px] font-bold ${
            r ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#fff8ee] text-[#7a4a10]"
          }`}
        >
          {r ? "Results ready" : "With the panel"}
        </span>
      </div>

      <LazyVideo
        label="Watch the showcase"
        load={async () => (await getUrl({ childId, entryId: entry.id }).unwrap()).data.url}
      />

      {!r && (
        <p className="text-sm text-[#5f6780]">
          The Future Smart Lab panel is watching the showcase. You'll get an email when the feedback, any award and
          the certificate are ready.
        </p>
      )}

      {r && (
        <>
          {r.award && (
            <div className="rounded-2xl bg-gradient-to-r from-[#fff4d6] to-[#ffe9e3] px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#a0620f]">Award</p>
              <p className="text-base font-bold text-[#5b3a10]"><Trophy className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{r.award}</p>
            </div>
          )}
          {r.feedback && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">From the panel</p>
              <p className="mt-1 whitespace-pre-line text-sm leading-6 text-[#16213e]">{r.feedback}</p>
            </div>
          )}
          <div className="space-y-2.5">
            <p className="text-xs font-bold uppercase tracking-wider text-[#8189a0]">
              Scores · {r.panelSize} panel member{r.panelSize === 1 ? "" : "s"}
            </p>
            {entry.criteria.map((c, i) => (
              <ScoreRow key={c} label={c} value={r.criterionAverages[i] ?? null} />
            ))}
          </div>
          <Link
            to={`/user/learn/${childId}/showcase/${entry.id}/certificate`}
            target="_blank"
            className="inline-block rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a]"
          >
            <GraduationCap className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Open the certificate
          </Link>
          <GalleryChoice childId={childId} entry={entry} isParent={isParent} />
        </>
      )}
    </div>
  );
}

/* ─── Gallery ────────────────────────────────────────────────────── */

function GalleryCard({ childId, item }: { childId: string; item: GalleryItem }) {
  const [getUrl] = useGetGalleryVideoUrlMutation();
  return (
    <div className="space-y-3 rounded-[24px] border border-[#e5e8ef] bg-white p-5">
      <div>
        <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
          {item.ageGroup} · {item.showcaseName}
        </p>
        <p className="text-base font-bold text-[#16213e]">{item.projectTitle}</p>
        <p className="text-xs text-[#5f6780]">
          by {item.firstName}
          {item.city ? `, ${item.city}` : ""}
          {item.isOwn ? " (you)" : ""} · premiered {fmtDay(item.premiereAt)}
        </p>
        {item.award && <p className="mt-1 text-xs font-semibold text-[#a0620f]"><Trophy className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{item.award}</p>}
      </div>
      <LazyVideo load={async () => (await getUrl({ childId, entryId: item.id }).unwrap()).data.url} />
    </div>
  );
}

function Gallery({ childId }: { childId: string }) {
  const [track, setTrack] = useState("");
  const { data, isLoading, error } = useGetGalleryQuery({ childId, track });
  const items = data?.data ?? [];
  return (
    <div className="space-y-4">
      <p className="rounded-2xl bg-[#f1f3f8] px-4 py-3 text-xs leading-5 text-[#3b4560]">
        Showcases from Future Smart Lab learners, shared with their parents' permission. Only signed-in academy families
        can see them. Please don't record, download or share these videos.
      </p>
      <div className="flex flex-wrap gap-2">
        {AGE_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setTrack(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              track === f.value ? "bg-[#1b2d5e] text-white" : "border border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {isLoading && <p className="text-sm text-[#8189a0]">Loading…</p>}
      {error && <p className="text-sm text-[#b94a3a]">{errorText(error, "Could not load the gallery")}</p>}
      {!isLoading && !error && items.length === 0 && (
        <div className="rounded-[24px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-8 text-center text-sm text-[#5f6780]">
          No showcases have premiered here yet.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((it) => (
          <GalleryCard key={it.id} childId={childId} item={it} />
        ))}
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */

export default function UserLearnShowcase() {
  const { childId = "" } = useParams();
  const role = useSelector(selectRole);
  const childMode = useChildMode();
  const isParent = role === "parent" && !childMode;
  const [tab, setTab] = useState<"mine" | "gallery">("mine");
  const { data, isLoading, error } = useGetShowcaseQuery(childId, { skip: !childId });
  const show = data?.data;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <Link to={`/user/learn/${childId}`} className="text-xs font-semibold text-[#1b2d5e]">
       {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Lessons
      </Link>
      <div>
        <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
          {show ? `${show.child.firstName}'s showcase` : "Showcase"}
        </h1>
        <p className="mt-1 text-sm text-[#5f6780]">
          At the end of each term you present at home and upload one video. The Future Smart Lab panel scores it and
          sends feedback and a certificate.
        </p>
      </div>

      <div className="flex gap-2 border-b border-[#e5e8ef]">
        {[
          { k: "mine", l: "My showcases" },
          { k: "gallery", l: "Gallery" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k as "mine" | "gallery")}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
              tab === t.k ? "border-[#1b2d5e] text-[#16213e]" : "border-transparent text-[#8189a0] hover:text-[#3b4560]"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "mine" && (
        <>
          {isLoading && <p className="text-sm text-[#8189a0]">Loading…</p>}
          {error && <p className="text-sm text-[#b94a3a]">{errorText(error, "Could not load the showcase")}</p>}
          {show && show.entries.length === 0 && (
            <div className="rounded-[24px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] p-8 text-center text-sm text-[#5f6780]">
              Nothing here yet. When {show.child.firstName} reaches a showcase week and hands in the showcase video, it
              comes here for the panel.
            </div>
          )}
          {show?.entries.map((e) => (
            <MyEntry key={e.id} childId={childId} entry={e} isParent={isParent} />
          ))}
        </>
      )}
      {tab === "gallery" && <Gallery childId={childId} />}
    </div>
  );
}
